import { occBaseSiteUrl, occFetch } from "~/app/api/occ/occFetch";
import { occCartUserPath } from "~/app/api/occ/occUserPath";
import {
  normalizeBulkUploadAddResponse,
  normalizeBulkUploadPreviewResponse,
  type BulkUploadAddResponse,
  type BulkUploadAddResponseWsDTO,
  type BulkUploadEntryIntentWsDTO,
  type BulkUploadPreviewResponse,
  type BulkUploadPreviewResponseWsDTO,
} from "../types/contracts";

export const CART_ALREADY_EXISTS_FOR_CONTEXT = "CART_ALREADY_EXISTS_FOR_CONTEXT";
export const CART_CONTEXT_CONFLICT = "CART_CONTEXT_CONFLICT";

export interface CartMergePreviewContextWsDTO {
  baseSiteId: string;
  soldTo: string;
  salesOrg: string;
  endCustomer?: string;
}

export interface CartMergeRequestWsDTO {
  previewContext: CartMergePreviewContextWsDTO;
  entries: BulkUploadEntryIntentWsDTO[];
}

export interface CartMergePreviewResponseWsDTO
  extends Omit<BulkUploadPreviewResponseWsDTO, "previewContext" | "trackingUid"> {
  previewContext?: CartMergePreviewContextWsDTO;
}

export interface CartMergePreviewResponse extends Omit<BulkUploadPreviewResponse, "previewContext" | "trackingUid"> {
  previewContext?: CartMergePreviewContextWsDTO;
}

export interface CreatedCartIdentity {
  code: string;
  guid?: string;
}

export class CartMergeApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`Cart merge request failed with status ${status}`);
    this.name = "CartMergeApiError";
    this.status = status;
    this.body = body;
  }
}

export function isCartAlreadyExistsForContext(error: unknown): error is CartMergeApiError {
  return isCartApiConflict(error, CART_ALREADY_EXISTS_FOR_CONTEXT);
}

export function isCartContextConflict(error: unknown): error is CartMergeApiError {
  return isCartApiConflict(error, CART_CONTEXT_CONFLICT);
}

export interface CartMergeOperations {
  preview(sourceCartGuid: string, signal?: AbortSignal): Promise<CartMergePreviewResponse>;
  createCart(signal?: AbortSignal): Promise<CreatedCartIdentity>;
  merge(
    activeCartCode: string,
    sourceCartGuid: string,
    request: CartMergeRequestWsDTO,
    signal?: AbortSignal,
  ): Promise<BulkUploadAddResponse>;
  discard(sourceCartGuid: string, signal?: AbortSignal): Promise<void>;
}

export const cartMergeOperations: CartMergeOperations = {
  preview: fetchCartMergePreview,
  createCart: createActiveCart,
  merge: mergeCart,
  discard: discardMergeSourceCart,
};

export async function fetchCartMergePreview(
  sourceCartGuid: string,
  signal?: AbortSignal,
): Promise<CartMergePreviewResponse> {
  const response = await occFetch(
    occBaseSiteUrl(`${occCartUserPath()}/carts/${encodeURIComponent(sourceCartGuid)}/mergePreview`),
    {
      method: "GET",
      signal,
    },
  );
  const body = await readJsonResponse<CartMergePreviewResponseWsDTO>(response);
  if (!response.ok && !(response.status === 400 && isBulkUploadPreviewResponse(body))) {
    throw new CartMergeApiError(response.status, body);
  }
  return normalizeCartMergePreviewResponse(body);
}

export async function createActiveCart(signal?: AbortSignal): Promise<CreatedCartIdentity> {
  const response = await occFetch(occBaseSiteUrl(`${occCartUserPath()}/carts`), {
    method: "POST",
    signal,
  });
  const body = await readJsonResponse<CreatedCartIdentity>(response);
  if (!response.ok || typeof body.code !== "string" || !body.code) {
    throw new CartMergeApiError(response.status, body);
  }
  return body;
}

export async function mergeCart(
  activeCartCode: string,
  sourceCartGuid: string,
  request: CartMergeRequestWsDTO,
  signal?: AbortSignal,
): Promise<BulkUploadAddResponse> {
  const response = await occFetch(
    occBaseSiteUrl(
      `${occCartUserPath()}/carts/${encodeURIComponent(activeCartCode)}/mergeWith/${encodeURIComponent(
        sourceCartGuid,
      )}`,
    ),
    {
      body: JSON.stringify(request),
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
      signal,
    },
  );
  const body = await readJsonResponse<BulkUploadAddResponseWsDTO>(response);
  if (!response.ok && !isBulkUploadResponse(body)) {
    throw new CartMergeApiError(response.status, body);
  }
  return normalizeBulkUploadAddResponse(body);
}

export async function discardMergeSourceCart(sourceCartGuid: string, signal?: AbortSignal): Promise<void> {
  const response = await occFetch(occBaseSiteUrl(`${occCartUserPath()}/carts/${encodeURIComponent(sourceCartGuid)}`), {
    method: "DELETE",
    signal,
  });
  if (!response.ok) {
    throw new CartMergeApiError(response.status, await readResponseBody(response));
  }
}

function normalizeCartMergePreviewResponse(response: CartMergePreviewResponseWsDTO): CartMergePreviewResponse {
  const normalized = normalizeBulkUploadPreviewResponse({
    outcome: response.outcome,
    ...(response.products ? { products: response.products } : {}),
    ...(response.globalError ? { globalError: response.globalError } : {}),
    ...(response.rowErrors ? { rowErrors: response.rowErrors } : {}),
  });
  return {
    outcome: normalized.outcome,
    products: normalized.products,
    rowErrors: normalized.rowErrors,
    ...(normalized.globalError ? { globalError: normalized.globalError } : {}),
    ...(response.previewContext ? { previewContext: response.previewContext } : {}),
  };
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  const body = await readResponseBody(response);
  if (body === undefined || body === null || typeof body !== "object" || Array.isArray(body)) {
    throw new CartMergeApiError(response.status, body);
  }
  return body as T;
}

function isBulkUploadResponse(body: BulkUploadAddResponseWsDTO): boolean {
  return typeof body.outcome === "string";
}

function isBulkUploadPreviewResponse(body: CartMergePreviewResponseWsDTO): boolean {
  return typeof body.outcome === "string";
}

function isCartApiConflict(error: unknown, errorCode: string): error is CartMergeApiError {
  return error instanceof CartMergeApiError && error.status === 409 && readOccErrorCode(error.body) === errorCode;
}

function readOccErrorCode(body: unknown): string | undefined {
  if (body === null || typeof body !== "object" || Array.isArray(body)) {
    return undefined;
  }
  const errors = (body as { errors?: unknown }).errors;
  if (!Array.isArray(errors) || errors.length === 0) {
    return undefined;
  }
  const error = errors[0];
  if (error === null || typeof error !== "object" || Array.isArray(error)) {
    return undefined;
  }
  const errorCode = (error as { errorCode?: unknown }).errorCode;
  return typeof errorCode === "string" ? errorCode : undefined;
}

async function readResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return undefined;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
